import React, { useState } from "react";
import { Box, Button, Card, Dropdown, Grid, Icon, Table, Text } from "~/components-shared";
import styled from "styled-components";
import { usePostHog } from "posthog-js/react";
import { useTranslation } from "react-i18next";
import StyledIcon from "~/components/StyledIcon";
import { InsightsLineItemItemStatusEnum, RoutesEnum, TopicSelectDropdownEnum } from "~/types/enums";
import WhatElseTableRow from "~/components/insightsWidgets/WhatElseTableRow";
const StyledTable = styled(Table)`
  width: ${_ref => {
  let {
    theme
  } = _ref;
  return `calc(100% + ${10 * theme.typography.fontStep}rem)`;
}};
`;
const StyledTableHeader = styled(Table.Header)`
  border-bottom: 1px solid ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.borders.primary;
}};

  th {
    padding-bottom: 1em;
  }
`;
const WhatElseCard = _ref3 => {
  let {
    widget,
    dateFrom,
    dateTo,
    isEditable = false,
    ...rest
  } = _ref3;
  const {
    t
  } = useTranslation();
  const {
    items
  } = widget.data;
  const itemsPerPage = 3;
  const [page, setPage] = useState(1);
  const [showDismissed, setShowDismissed] = React.useState(false);
  const filteredItems = items.filter(item => !item.isClosed && !item.isDismissed || showDismissed && item.isDismissed);
  const posthog = usePostHog();
  const totalPageCount = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const handleNextPageClick = () => {
    setPage(prev => prev + 1);
    posthog.capture(`Insights What Else – Next Page Click`, {
      from: page
    });
  };
  const handlePrevPageClick = () => {
    setPage(prev => prev - 1);
    posthog.capture(`Insights What Else – Next Page Click`, {
      from: page
    });
  };
  const handleViewAll = () => {
    const params = new URLSearchParams();
    params.append("filter[statuses][]", InsightsLineItemItemStatusEnum.OPEN);
    params.append("filter[dateFrom]", dateFrom);
    params.append("filter[dateTo]", dateTo);
    params.append("filter[topicIds][]", TopicSelectDropdownEnum.NO_TOPICS);
    const url = `${window.location.origin}${RoutesEnum.PROGRESS_UPDATES}?${params.toString()}`;
    window.open(url, "_self");
  };
  return /*#__PURE__*/React.createElement(Card, rest, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: "1fr auto",
    gap: 2,
    align: "center"
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    fontSize: 8,
    s: {
      mb: 3
    }
  }, t("WhatElseCard.whatElse"))), /*#__PURE__*/React.createElement(Box, {
    align: "center",
    gap: 3
  }, filteredItems.length > itemsPerPage && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    onClick: handlePrevPageClick,
    color: "transparent",
    disabled: page === 1,
    s: {
      p: 1
    },
    outline: false,
    rounded: true
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 5,
    display: "block",
    rotate: -90,
    src: "chevron"
  })), /*#__PURE__*/React.createElement(Text, {
    weight: "medium",
    color: "textSecondary"
  }, page, " of ", totalPageCount), /*#__PURE__*/React.createElement(Button, {
    onClick: handleNextPageClick,
    color: "transparent",
    disabled: totalPageCount <= 1 || page >= totalPageCount,
    s: {
      p: 1
    },
    outline: false,
    rounded: true
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 5,
    display: "block",
    rotate: 90,
    src: "chevron"
  }))), isEditable && /*#__PURE__*/React.createElement(Dropdown, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("common.actions.options"),
    display: "inline-block",
    direction: "bottomLeft",
    handlerProps: {
      color: showDismissed ? "translucentPrimary" : "translucent",
      s: {
        p: 2
      }
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      display: "block",
      src: "filter"
    })
  }, _ref4 => {
    let {
      isOpen,
      onItemClick
    } = _ref4;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        onItemClick();
        setShowDismissed(prev => !prev);
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: showDismissed ? "checkboxFill" : "checkbox",
      s: {
        mr: 2
      },
      color: "primary"
    }), "Show Dismissed"));
  }), /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("FindingsCard.viewAllActionItems"),
    onClick: handleViewAll,
    color: "translucent",
    s: {
      pl: 6,
      pr: 4,
      py: 2
    },
    fontSize: 3.5,
    elemAfter: /*#__PURE__*/React.createElement(Icon, {
      src: "arrow",
      s: {
        mt: -0.5,
        ml: 1
      }
    })
  }, t("common.actions.viewAll")))), filteredItems.length ? /*#__PURE__*/React.createElement(StyledTable, {
    size: "m",
    fontSize: 3.2,
    tableProps: {
      style: {
        minWidth: "1000px"
      }
    },
    s: {
      mx: -5,
      mb: -5
    }
  }, /*#__PURE__*/React.createElement(StyledTableHeader, null, /*#__PURE__*/React.createElement(Table.Tr, null, /*#__PURE__*/React.createElement(Table.Th, {
    width: "0"
  }), /*#__PURE__*/React.createElement(Table.Th, null, t("WhatElseCard.context")), /*#__PURE__*/React.createElement(Table.Th, null, t("WhatElseCard.action")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "150px"
  }, t("WhatElseCard.owner")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "100px"
  }, t("WhatElseCard.urgency")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "120px"
  }, t("WhatElseCard.observedAt")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "30px"
  }), /*#__PURE__*/React.createElement(Table.Th, {
    width: "0"
  }))), /*#__PURE__*/React.createElement(Table.Body, null, paginatedItems.map(item => /*#__PURE__*/React.createElement(WhatElseTableRow, {
    showDismissed: showDismissed,
    isEditable: isEditable,
    key: item.id,
    item: item
  })))) : /*#__PURE__*/React.createElement(Box, {
    direction: "column",
    justify: "center",
    align: "center",
    fullHeight: true
  }, /*#__PURE__*/React.createElement(StyledIcon, {
    src: "infoOutline",
    color: "translucentInfo"
  }), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    weight: "semiBold",
    display: "block",
    s: {
      mt: 6
    },
    align: "center",
    style: {
      maxWidth: "300px"
    }
  }, t("WhatElseCard.noItems"))));
};
export default WhatElseCard;